import * as React from 'react'
import { Checks, Gear, ListChecks, SquaresFour } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Badge, type BadgeColor } from '../core/badge'
import { Button } from '../core/button'
import { Card } from '../core/card'
import { HelpPopover } from '../core/help-popover'
import { Link } from '../core/link'
import { Separator } from '../core/separator'
import { Stat } from '../core/stat'
import {
  SegmentedControl,
  type SegmentedControlOption,
} from '../core/segmented-control'
import {
  entityTypeConfig,
  type EntityRef,
  type EntityType,
} from '../core/entity-glyph'
import {
  ActionCenterCard,
  toCardEntity,
  PRIORITY_META,
  PRIORITY_ORDER,
  type ActionPriority,
} from './action-center-card'

/**
 * ActionCenter — the home page's AI "here's what needs your attention" feed,
 * rebuilt as a self-contained block. It's the read-and-triage surface (a scannable
 * list of prioritized {@link ActionCenterCard}s); the companion {@link
 * ActionCenterPanel} is the act-on-one *drawer* that a card's **View** opens. They
 * share primitives but are different surfaces.
 *
 *   <ActionCenter
 *     items={recommendations}
 *     defaultGroupBy="priority"
 *     completedCount={1}
 *     totalCount={13}
 *     onView={openPanel}
 *     onDismiss={dismiss}
 *     onManageAgents={openAgentSettings}
 *     onViewCompleted={showCompleted}
 *   />
 *
 * **Anatomy** (mirrors the current app):
 *  - an optional title/subtitle block (hidden under a tab that already names the
 *    feed — see `title`);
 *  - a two-row header — row 1 leads with the summary stat pills (each with a
 *    {@link HelpPopover}) and trails a "Completed" count button; row 2 carries the
 *    **Group by** {@link SegmentedControl} (Priority · Type · Company) and a
 *    "Manage AI Agents" link. The stat pills track the grouping: the three
 *    priority buckets (Hot / Today / This Week) under Priority, else an aggregate
 *    (# of groups + total);
 *  - the grouped {@link ActionCenterCard}s.
 *
 * Grouping is controlled (`groupBy` + `onGroupByChange`) or uncontrolled
 * (`defaultGroupBy`). The block is presentational — it groups and renders the
 * `items` you pass; the consumer owns fetching, dismissing, and completion.
 */

export type { ActionPriority }

/** How the feed is grouped. */
export type ActionCenterGroupBy = 'priority' | 'type' | 'company'

export interface ActionCenterItem {
  /** Stable id — what `onView` / `onDismiss` carry. */
  id: string
  /** The recommendation headline (the AI's action sentence). */
  title: React.ReactNode
  /** The AI "why" — one short paragraph under the title. */
  description?: React.ReactNode
  /** Urgency — sets the priority badge + which stat pill + priority grouping. */
  priority: ActionPriority
  /** Record type of the action's subject — the "Group by Type" key + label. */
  type?: EntityType
  /** Company this action belongs to — the "Group by Company" key. */
  company?: string
  /** Linked records shown in the card footer (deal / company / contact …). */
  entities?: EntityRef[]
  /** Show the AI "recommendation" mark. Default `true`. */
  ai?: boolean
}

/** Default per-priority stat-pill help copy. */
const PRIORITY_HELP: Record<ActionPriority, React.ReactNode> = {
  hot: 'Time-sensitive — a stalling deal or an unanswered thread that needs attention now.',
  today: 'Best handled today to keep the deal moving.',
  thisWeek: 'On the radar for this week — no rush today.',
}

const GROUP_BY_OPTIONS: SegmentedControlOption[] = [
  { value: 'priority', label: 'Priority' },
  { value: 'type', label: 'Type' },
  { value: 'company', label: 'Company' },
]

interface FeedGroup {
  key: string
  label: React.ReactNode
  /** Leading eyebrow glyph (priority accent icon / muted type glyph). */
  icon?: React.ReactNode
  items: ActionCenterItem[]
}

/** Split items into ordered, labeled groups per the active `groupBy`. */
function groupItems(
  items: ActionCenterItem[],
  groupBy: ActionCenterGroupBy
): FeedGroup[] {
  if (groupBy === 'priority') {
    return PRIORITY_ORDER.map((priority) => {
      const meta = PRIORITY_META[priority]
      return {
        key: priority,
        label: meta.label,
        // Group eyebrows are subtle/neutral — the color lives on the card badges,
        // not the section headers.
        icon: (
          <span className="flex text-fbui-gray-400 [&>svg]:size-3.5">
            {meta.icon}
          </span>
        ),
        items: items.filter((item) => item.priority === priority),
      }
    }).filter((group) => group.items.length > 0)
  }

  // Type / Company — bucket by key, preserving first-seen order.
  const buckets = new Map<string, ActionCenterItem[]>()
  const order: string[] = []
  for (const item of items) {
    const key =
      groupBy === 'type' ? item.type ?? '__other' : item.company ?? '__none'
    if (!buckets.has(key)) {
      buckets.set(key, [])
      order.push(key)
    }
    buckets.get(key)!.push(item)
  }
  return order.map((key) => {
    const groupItemsForKey = buckets.get(key)!
    if (groupBy === 'type') {
      const isOther = key === '__other'
      return {
        key,
        label: isOther ? 'Other' : entityTypeConfig[key as EntityType].label,
        icon: isOther ? undefined : (
          <span className="flex text-fbui-gray-400 [&>svg]:size-3.5">
            {entityTypeConfig[key as EntityType].icon}
          </span>
        ),
        items: groupItemsForKey,
      }
    }
    return {
      key,
      label: key === '__none' ? 'No company' : key,
      items: groupItemsForKey,
    }
  })
}

export interface ActionCenterProps {
  /** Header title. Default "Action Center". Pass `null` to hide it — e.g. when the
   *  feed sits under a tab already labeled "Action Center". */
  title?: React.ReactNode
  /** Header subtitle. Default "Here's what needs your attention". */
  subtitle?: React.ReactNode
  /** The recommendations to triage. */
  items: ActionCenterItem[]
  /** Active grouping (controlled). Pair with `onGroupByChange`. */
  groupBy?: ActionCenterGroupBy
  /** Initial grouping (uncontrolled). Default "priority". */
  defaultGroupBy?: ActionCenterGroupBy
  onGroupByChange?: (groupBy: ActionCenterGroupBy) => void
  /** Open a recommendation (typically the {@link ActionCenterPanel} drawer). */
  onView?: (item: ActionCenterItem) => void
  /** Dismiss a recommendation from the feed. */
  onDismiss?: (item: ActionCenterItem) => void
  /** Per-card primary action label. Default "View". */
  viewLabel?: string
  /** Per-card secondary action label. Default "Dismiss". */
  dismissLabel?: string
  /** Completed-today count for the "Completed" button badge (e.g. `1` of `13`). */
  completedCount?: number
  /** Total-today count for the "Completed" button badge. */
  totalCount?: number
  /** Wire the "Completed" button; hidden when omitted. */
  onViewCompleted?: () => void
  /** Wire the "Manage AI Agents" link; hidden when omitted. */
  onManageAgents?: () => void
  /** Override the default per-priority stat-pill help copy. */
  priorityHelp?: Partial<Record<ActionPriority, React.ReactNode>>
  /** Shown in place of the feed when there are no items. */
  emptyState?: React.ReactNode
  className?: string
}

export const ActionCenter = React.forwardRef<HTMLDivElement, ActionCenterProps>(
  (
    {
      title = 'Action Center',
      subtitle = "Here's what needs your attention",
      items,
      groupBy: groupByProp,
      defaultGroupBy = 'priority',
      onGroupByChange,
      onView,
      onDismiss,
      viewLabel,
      dismissLabel,
      completedCount,
      totalCount,
      onViewCompleted,
      onManageAgents,
      priorityHelp,
      emptyState = 'All caught up — nothing needs your attention right now.',
      className,
    },
    ref
  ) => {
    const isControlled = groupByProp !== undefined
    const [internalGroupBy, setInternalGroupBy] =
      React.useState<ActionCenterGroupBy>(defaultGroupBy)
    const groupBy = isControlled ? groupByProp : internalGroupBy
    const setGroupBy = (next: ActionCenterGroupBy) => {
      if (!isControlled) setInternalGroupBy(next)
      onGroupByChange?.(next)
    }

    const counts = React.useMemo(() => {
      const tally: Record<ActionPriority, number> = { hot: 0, today: 0, thisWeek: 0 }
      for (const item of items) tally[item.priority] += 1
      return tally
    }, [items])

    // The summary pills track the active grouping: the three priority buckets
    // when grouped by priority, else an aggregate (# of groups + total) since
    // there are too many companies/types to show one pill each.
    const statPills = React.useMemo<
      {
        key: string
        color: BadgeColor
        icon: React.ReactNode
        value: number
        label: string
        help: React.ReactNode
      }[]
    >(() => {
      if (groupBy === 'priority') {
        return PRIORITY_ORDER.map((priority) => ({
          key: priority,
          color: PRIORITY_META[priority].color,
          icon: PRIORITY_META[priority].icon,
          value: counts[priority],
          label: PRIORITY_META[priority].label,
          help: priorityHelp?.[priority] ?? PRIORITY_HELP[priority],
        }))
      }
      const total = items.length
      // Aggregate counts aren't urgency, so they take the calm brand green rather
      // than a priority hue.
      const totalPill = {
        key: 'total',
        color: 'primary' as BadgeColor,
        icon: <Checks aria-hidden="true" />,
        value: total,
        label: 'Total',
        help: 'Total open actions across all groups.',
      }
      if (groupBy === 'company') {
        const companies = new Set(
          items
            .map((item) => item.company)
            .filter((company): company is string => company != null)
        ).size
        return [
          {
            key: 'companies',
            color: 'primary',
            icon: entityTypeConfig.company.icon,
            value: companies,
            label: 'Companies',
            help: 'Distinct companies with open actions.',
          },
          totalPill,
        ]
      }
      const types = new Set(
        items
          .map((item) => item.type)
          .filter((type): type is EntityType => type != null)
      ).size
      return [
        {
          key: 'types',
          color: 'primary',
          icon: <SquaresFour aria-hidden="true" />,
          value: types,
          label: 'Types',
          help: 'Distinct action types with open actions.',
        },
        totalPill,
      ]
    }, [groupBy, counts, items, priorityHelp])

    const groups = React.useMemo(
      () => groupItems(items, groupBy),
      [items, groupBy]
    )

    return (
      <Card ref={ref} className={cn('w-full overflow-hidden', className)}>
        {/* Header — an optional title block (standalone use), then one row with
            the priority counts leading and the grouping + top actions on the right. */}
        <div className="p-6">
          {(title != null || subtitle != null) && (
            <div className="mb-5">
              {title != null && (
                <h2 className="text-xl font-bold text-fbui-gray-900">{title}</h2>
              )}
              {subtitle != null && (
                <p
                  className={cn(
                    'text-sm text-fbui-gray-500',
                    title != null && 'mt-0.5'
                  )}
                >
                  {subtitle}
                </p>
              )}
            </div>
          )}

          {/* Row 1 — summary: the open counts (which change with the grouping)
              on the left, the completed-count button on the right. */}
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              {statPills.map((pill, index) => (
                <React.Fragment key={pill.key}>
                  {index > 0 && (
                    <Separator
                      orientation="vertical"
                      decorative
                      className="h-7"
                    />
                  )}
                  <Stat
                    density="inline"
                    size="sm"
                    color={pill.color}
                    icon={pill.icon}
                    value={pill.value}
                    label={pill.label}
                    help={
                      <HelpPopover
                        triggerSize="sm"
                        label={`About ${pill.label}`}
                      >
                        {pill.help}
                      </HelpPopover>
                    }
                  />
                </React.Fragment>
              ))}
            </div>
            {onViewCompleted != null && (
              <Button
                appearance="outline"
                variant="primary"
                size="sm"
                leftIcon={<ListChecks />}
                onClick={onViewCompleted}
              >
                Completed
                {totalCount != null && (
                  <Badge color="gray" size="sm" className="ml-1.5">
                    {completedCount ?? 0}/{totalCount}
                  </Badge>
                )}
              </Button>
            )}
          </div>

          {/* Row 2 — controls: grouping on the left, settings on the right. */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-fbui-gray-500">Group by</span>
              <SegmentedControl
                aria-label="Group by"
                size="xs"
                variant="subtle"
                options={GROUP_BY_OPTIONS}
                value={groupBy}
                onValueChange={(value) =>
                  setGroupBy(value as ActionCenterGroupBy)
                }
              />
            </div>
            {onManageAgents != null && (
              <Link asChild variant="primary" size="xs">
                <button type="button" onClick={onManageAgents}>
                  <Gear aria-hidden="true" />
                  Manage AI Agents
                </button>
              </Link>
            )}
          </div>
        </div>

        {/* Feed — grouped recommendation cards on a recessed surface. */}
        <div className="space-y-6 border-t border-fbui-gray-200 bg-fbui-gray-50 p-6">
          {groups.length === 0 ? (
            <p role="status" className="py-2 text-sm text-fbui-gray-500">
              {emptyState}
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.key}>
                {/* h3 (under the "Action Center" h2) so the priority/type/company
                    groups are navigable by heading. */}
                <h3 className="mb-2 flex items-center gap-1.5 px-0.5 text-xs font-semibold uppercase tracking-wide text-fbui-gray-500">
                  {group.icon}
                  {group.label}
                </h3>
                <div className="space-y-3">
                  {group.items.map((item) => (
                    <ActionCenterCard
                      key={item.id}
                      size="sm"
                      title={item.title}
                      description={item.description}
                      priority={item.priority}
                      ai={item.ai}
                      entities={item.entities?.map(toCardEntity)}
                      onView={onView ? () => onView(item) : undefined}
                      onDismiss={onDismiss ? () => onDismiss(item) : undefined}
                      viewLabel={viewLabel}
                      dismissLabel={dismissLabel}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    )
  }
)
ActionCenter.displayName = 'ActionCenter'
